import ContactLink from "@/components/ContactLink";
import { Link } from "@inertiajs/react";
import { Mail } from "lucide-react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function ReceiptEmail() {
  return (
    <Card>
      <CardContent className="p-5 space-y-3 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Mail className="h-4 w-4" />
          <strong className="text-foreground">PagePledge</strong>
          <span>&lt;hello@pagepledge.com&gt;</span>
        </div>
        <p className="font-display text-base font-semibold text-foreground">Thanks for supporting Sarah T.</p>
        <p className="text-muted-foreground">Lincoln Elementary · Spring Read-a-thon 2026</p>
        <div className="space-y-1 pt-2 border-t border-border">
          <div className="flex justify-between"><span>Donation</span><span className="font-mono tabular-nums">$50.00</span></div>
          <div className="flex justify-between"><span>Card fee covered</span><span className="font-mono tabular-nums">+ $1.75</span></div>
          <div className="flex justify-between font-semibold"><span>Total</span><span className="font-mono tabular-nums">$51.75</span></div>
        </div>
        <Button variant="outline">View receipt online</Button>
      </CardContent>
    </Card>
  );
}

export default function DonorReceiptsAndRefunds() {
  return (
    <>
      <p>
        After you donate, we'll email you a receipt — and the thank-you page has a link to the same receipt that you can bookmark for later. If something went wrong (wrong amount, donated twice, changed your mind), the school can refund you.
      </p>

      <h2>The receipt</h2>
      <p>
        Within seconds of donating, you'll get an email from hello@pagepledge.com with the school name, the student, the amount, and a link to the receipt online. The school's contact email is on it too, in case you have questions.
      </p>

      <Screenshot caption="A donation receipt">
        <div className="max-w-md mx-auto">
          <ReceiptEmail />
        </div>
      </Screenshot>

      <Callout variant="note" title="Not a tax receipt">
        <p>
          Your receipt is for your records. It isn't a charitable tax receipt, and PagePledge doesn't issue them.
        </p>
      </Callout>

      <h2>Can't find your receipt?</h2>
      <p>
        Search your inbox for "pagepledge" — it's sent right away. Check your spam and promotions folders too. If you saved the link from the thank-you page, that still works. If you can't find it anywhere, email <ContactLink>hello@pagepledge.com</ContactLink> and we'll get you a copy.
      </p>

      <h2>How to get a refund</h2>
      <p>
        Email the school directly — their contact email is on your receipt. A short message ("I accidentally donated $50 instead of $5 to Sarah T., can you refund and I'll redo it?") is plenty. They'll process the refund through Square, and the money will be back on your card in 5–10 business days. There's nothing to install or click on your end.
      </p>

      <Callout variant="tip" title="Schools usually move fast">
        <p>
          Most refund requests are handled the same day. If you've waited more than a couple of business days, email us at <ContactLink>hello@pagepledge.com</ContactLink> and we'll help nudge things along.
        </p>
      </Callout>

      <p>
        You could also dispute the charge with your bank — but please email the school first. A dispute costs them an extra fee on top of the lost donation, while a refund just returns the donation. Either way, your money comes back to you.
      </p>

      <p>
        If you used the wrong card, the simplest fix is to ask for a refund and donate again with the right one — there's no way to change the card on a donation that's already gone through.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/making-a-donation">How to make a donation</Link>.</li>
        <li><Link href="/help/articles/anonymous-donations-and-messages">Anonymous donations and messages</Link>.</li>
      </ul>
    </>
  );
}
